import { useCallback, useEffect, useRef, useState } from "react";

export const SOUND_STORAGE_KEY = "abTagging.videoSoundOn";

const NOTHING_PLAYING = { index: -1, isPlaying: false };

const readStoredFlag = (key, fallback) => {
  try {
    const stored = window.localStorage.getItem(key);
    return stored === null ? fallback : stored === "true";
  } catch (error) {
    return fallback;
  }
};

const storeFlag = (key, value) => {
  try {
    window.localStorage.setItem(key, String(value));
  } catch (error) {
    // Preferences are best effort; private browsing can reject storage writes.
  }
};

/**
 * Drives A/B video playback in two mutually exclusive modes:
 *
 * - alternating (autoAlternate on): one side plays at a time and hands off to
 *   the other when it ends, so left and right are heard in turn.
 * - hover (autoAlternate off): the hovered side plays and pauses where it left
 *   off when the pointer leaves.
 *
 * Nothing plays until the viewer clicks a video or the toggle, because browsers
 * refuse unmuted playback before the page has been interacted with.
 */
const useVideoPlayback = ({ videoRefs, isVideo, files }) => {
  const fileCount = files.length;

  const [autoAlternate, setAutoAlternate] = useState(false);
  const [soundOn, setSoundOn] = useState(() => readStoredFlag(SOUND_STORAGE_KEY, true));
  const [hasStarted, setHasStarted] = useState(false);
  const [playing, setPlaying] = useState(NOTHING_PLAYING);

  // Media event handlers and the pair-change effect need current values without
  // being rebuilt (or re-run) every time one of them changes.
  const latest = useRef({ autoAlternate, soundOn, hasStarted, playing });
  latest.current = { autoAlternate, soundOn, hasStarted, playing };

  const applyElementFlags = useCallback(
    (activeIndex, sound, alternate) => {
      videoRefs.current.forEach((video, index) => {
        if (!video) return;
        video.muted = !(sound && index === activeIndex);
        // Looping would swallow the `ended` event the alternating handoff needs.
        video.loop = !alternate;
      });
    },
    [videoRefs]
  );

  const pauseAllVideos = useCallback(
    ({ rewind = false } = {}) => {
      videoRefs.current.forEach((video) => {
        if (!video) return;
        video.pause();
        if (rewind) video.currentTime = 0;
      });
    },
    [videoRefs]
  );

  const playOne = useCallback(
    (index, options = {}) => {
      const video = videoRefs.current[index];
      if (!video) return;

      const sound = options.sound ?? latest.current.soundOn;
      const alternate = options.alternate ?? latest.current.autoAlternate;

      videoRefs.current.forEach((other, otherIndex) => {
        if (other && otherIndex !== index) other.pause();
      });

      applyElementFlags(index, sound, alternate);
      if (options.fromStart) video.currentTime = 0;

      const started = video.play();
      if (started && typeof started.catch === "function") {
        started.catch(() => {
          // The browser refused audible playback; drop to muted so the
          // comparison still runs and the speaker icon tells the truth.
          video.muted = true;
          setSoundOn(false);
          storeFlag(SOUND_STORAGE_KEY, false);
          video.play().catch(() => {});
        });
      }
    },
    [applyElementFlags, videoRefs]
  );

  const handlePlay = useCallback(
    (index) => {
      const video = videoRefs.current[index];
      if (!latest.current.hasStarted) {
        // Native controls dispatch `play` without dispatching the React click
        // handler, so this event is the first-interaction boundary.
        setHasStarted(true);
        latest.current = { ...latest.current, hasStarted: true };
        if (video) video.currentTime = 0;
      }
      applyElementFlags(index, latest.current.soundOn, latest.current.autoAlternate);
      setPlaying({ index, isPlaying: true });
      // Whatever started this (hover, handoff, native controls), only one side
      // is ever audible.
      videoRefs.current.forEach((other, otherIndex) => {
        if (other && otherIndex !== index && !other.paused) other.pause();
      });
    },
    [applyElementFlags, videoRefs]
  );

  const handlePause = useCallback((index) => {
    setPlaying((prev) => (prev.index === index ? { ...prev, isPlaying: false } : prev));
  }, []);

  const handleEnded = useCallback(
    (index) => {
      if (!latest.current.autoAlternate || fileCount < 2) return;
      const finished = videoRefs.current[index];
      if (finished) finished.currentTime = 0;
      playOne((index + 1) % fileCount, { fromStart: true });
    },
    [fileCount, playOne, videoRefs]
  );

  const handleCanPlay = useCallback(
    (index) => {
      const { autoAlternate: alternate, hasStarted: started, playing: current } = latest.current;
      // A new pair finished loading while alternating: resume from the left side.
      if (index === 0 && alternate && started && current.index === -1) {
        playOne(0, { fromStart: true });
      }
    },
    [playOne]
  );

  const handleMouseEnter = useCallback(
    (index) => {
      if (latest.current.autoAlternate || !latest.current.hasStarted) return;
      playOne(index);
    },
    [playOne]
  );

  const handleMouseLeave = useCallback(
    (index) => {
      if (latest.current.autoAlternate || !latest.current.hasStarted) return;
      const video = videoRefs.current[index];
      // Keep the position so hovering again picks up where it stopped.
      if (video) video.pause();
    },
    [videoRefs]
  );

  const handleClick = useCallback(
    (index) => {
      // After the first click the native controls and hover take over.
      if (latest.current.hasStarted) return;
      setHasStarted(true);
      latest.current.hasStarted = true;
      playOne(index, { fromStart: true });
    },
    [playOne]
  );

  const handleDoubleClick = useCallback(
    (index) => {
      setHasStarted(true);
      latest.current = { ...latest.current, hasStarted: true };
      playOne(index, { fromStart: true });
    },
    [playOne]
  );

  const toggleAutoAlternate = useCallback(() => {
    const nextAlternate = !latest.current.autoAlternate;
    setAutoAlternate(nextAlternate);
    // Clicking the toggle is itself the gesture that unlocks audible playback.
    setHasStarted(true);
    latest.current = { ...latest.current, autoAlternate: nextAlternate, hasStarted: true };

    if (nextAlternate) {
      playOne(0, { fromStart: true, alternate: true });
    } else {
      pauseAllVideos();
      applyElementFlags(latest.current.playing.index, latest.current.soundOn, false);
    }
  }, [applyElementFlags, pauseAllVideos, playOne]);

  const toggleSound = useCallback(() => {
    const nextSound = !latest.current.soundOn;
    setSoundOn(nextSound);
    storeFlag(SOUND_STORAGE_KEY, nextSound);
    latest.current = { ...latest.current, soundOn: nextSound };
    applyElementFlags(latest.current.playing.index, nextSound, latest.current.autoAlternate);
  }, [applyElementFlags]);

  const togglePlayPause = useCallback(() => {
    const { hasStarted: started, playing: current } = latest.current;
    if (!started) {
      setHasStarted(true);
      latest.current.hasStarted = true;
      playOne(0);
      return;
    }

    const index = current.index === -1 ? 0 : current.index;
    const video = videoRefs.current[index];
    if (!video) return;
    if (video.paused) {
      playOne(index);
    } else {
      video.pause();
    }
  }, [playOne, videoRefs]);

  // Keep mute and loop in step with the current mode and active side, including
  // after React swaps in the elements for a new pair.
  useEffect(() => {
    if (!isVideo) return;
    applyElementFlags(playing.index, soundOn, autoAlternate);
  }, [applyElementFlags, autoAlternate, files, isVideo, playing.index, soundOn]);

  // A new pair always starts stopped at the first frame. Alternating mode picks
  // it back up from `handleCanPlay` once the left video has data.
  useEffect(() => {
    if (!isVideo) return;
    pauseAllVideos({ rewind: true });
    setPlaying(NOTHING_PLAYING);
  }, [files, isVideo, pauseAllVideos]);

  const isPlaying = (index) => playing.index === index && playing.isPlaying;
  const isAudible = (index) => soundOn && playing.index === index;

  return {
    autoAlternate,
    soundOn,
    hasStarted,
    isPlaying,
    isAudible,
    toggleAutoAlternate,
    toggleSound,
    togglePlayPause,
    handlePlay,
    handlePause,
    handleEnded,
    handleCanPlay,
    handleClick,
    handleDoubleClick,
    handleMouseEnter,
    handleMouseLeave,
  };
};

export default useVideoPlayback;
